<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import _ from 'lodash';
import { useNotification } from "@kyvg/vue3-notification";
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import StreamService from '@/services/StreamService';
import IconLivestream from '@/components/icon/IconLivestream.vue';
import IconMultistream from '@/components/icon/IconMultistream.vue';
import StreamRegion from '../ui/StreamRegion.vue';
import FloatingInput from '../ui/FloatingInput.vue';
import FormButton from '../Atoms/FormButton.vue';
import Sidebar from '../modals/Sidebar'
import SidebarItem from './SidebarItem'
import Alert from '../Atoms/Alert.vue';
import SidebarTrial from './SidebarTrial.vue';
import Tooltip from '../Atoms/Tooltip.vue'
import { track, EVENTS } from '@/lib/tracking';
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'


const props = defineProps({
  streamType: {
    type: String,
  }
})

const store = useStore()
const { notify } = useNotification()
const router = useRouter()
const route = useRoute()

const sidebar = ref(null)
const nameInput = ref(null)
const stream = ref({
	name: '',
	type: null,
	uiRegion: null,
})

const defaulStage = ref(null)
const regions = ref(null)

const isLoading = ref(false)

const error = ref(null)

const emit = defineEmits({
	'new-channel': (stream, region) => { return stream, region }
})

const hasMultistreamSubscription = computed(() => store.getters['User/hasMultistreamSubscription'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const hasPreRecordedSubscription = computed(() => store.getters['User/hasPreRecordedSubscription'])
const multiStreams = computed(() => store.getters['Streams/multiStreams'] || [])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'] || [])
const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])

const restreamLimit = computed(() => {
	const c = _.get(baseSubscription.value, 'definitionOverride.streams', _.get(baseSubscription.value, 'package.definition.streams', 0))
	return typeof c === 'number' && c > 0 ? c : null
})

const liveLimit = computed(() => {
	const livePackage = addonSubscriptions.value?.find((subscription) => subscription.package?.category === 'live' && subscription.enabled)
	const c = _.get(livePackage, 'definitionOverride.streams', _.get(livePackage, 'package.definition.streams', 0))
	return typeof c === 'number' && c > 0 ? c : null
})

const atMultistreamLimit = computed(() => {
	const max = restreamLimit.value
	if (!max) return false
	return multiStreams.value.length >= max
})

const atLivestreamLimit = computed(() => {
	const max = liveLimit.value
	if (!max) return false
	return allInOneStreams.value.length >= max
})

const atSelectedTypeLimit = computed(() => {
	if (stream.value.type === 'restream') return atMultistreamLimit.value
	if (stream.value.type === 'live') return atLivestreamLimit.value
	return false
})

const selectedTypeLimitMessage = computed(() => {
	if (stream.value.type === 'restream') return planLimitReachedMessage('Multistream')
	if (stream.value.type === 'live') return planLimitReachedMessage('Livestream')
	return ''
})

watch([hasMultistreamSubscription], () => {
	if (route.name !== 'Scheduler') {
		if (!hasMultistreamSubscription.value) {
			defaulStage.value = 'live'
		} else {
			defaulStage.value = null
		}
		stream.value.type = defaulStage.value
	}
}, { immediate: true })

onMounted(async () => {
	regions.value = await StreamService.getAvailableRegions('list');
	 if (props.streamType) {
		defaulStage.value = props.streamType
		stream.value.type = props.streamType
	}
	if (props.streamType === 'scheduled') {
		const region = _.head(regions.value.filter(r => r.platforms.indexOf('scheduled') > -1))
		stream.value.uiRegion = region
	}
})

const toggleSidebar = () => sidebar.value.toggleSidebar()

const openForType = (type) => {
	if (!['live', 'restream', 'scheduled'].includes(type)) return
	if (type === 'restream' && atMultistreamLimit.value) return
	if (type === 'live' && atLivestreamLimit.value) return
	stream.value.type = type
	toggleSidebar()
}

const selectRegion = (region) => stream.value.uiRegion = region

const setStreamType = (type) => {
	if (type === 'restream' && atMultistreamLimit.value) return
	if (type === 'live' && atLivestreamLimit.value) return
	stream.value.type = type
}

const currentStage = computed(() => stream.value.type ? 2 : 1)

const clearData = () => {
	stream.value.type = defaulStage.value
	stream.value.uiRegion = null
	error.value = null
}

const title = computed(() => {
	if (stream.value.type === 'restream') return 'Multistream'
	if (stream.value.type === 'live' && !hasMultistreamSubscription.value) return 'Livestream'
	if (stream.value.type === 'live' && hasMultistreamSubscription.value) return 'Live Stream'
	if (stream.value.type === 'scheduled') return 'Pre-recorded Stream'
})

const description = computed(() => {
	if (stream.value.type === 'restream') return 'Stream your videos to multiple social platforms and destinations at the same time.'
	if (stream.value.type === 'live') return 'Livestream to your own website and multiple destinations.'
	if (stream.value.type === 'scheduled') return 'Schedule your pre-recorded video to broadcast on multiple social platforms and destinations at the same time.'
})

const shouldShowRegion = computed(() => {
	if (stream.value.type === 'restream') return true
	if (stream.value.type === 'live') return true
	return false
})
const goToStage = (stage) => {
	if (stage === 1) stream.value.type = null
}
const isDisabled = computed(() => {
	return atSelectedTypeLimit.value || (!stream.value.uiRegion && shouldShowRegion.value) || stream.value.name.trim().length === 0
})

const onSidebarOpened = () => {
	// Focus the name field so the user can start typing immediately
	nextTick(() => nameInput.value?.focus())
}

const onSubmit = () => {
	if (!isDisabled.value && !isLoading.value) createStream()
}

const computedStage = computed(() => !hasMultistreamSubscription.value || (props.streamType === 'scheduled') ? 'stage-one' : 'stage-two')

const isMultiStage = computed(() => hasMultistreamSubscription.value && props.streamType !== 'scheduled')

const showTrial = computed(() => {
	if (stream.value.type === 'scheduled') {
		return !hasPreRecordedSubscription.value && currentStage.value === 2
	}
	return !hasLiveSubscription.value && currentStage.value === 2 && stream.value.type === 'live'
})
const createStream = async () => {
	try {
		isLoading.value = true
		const newStream = await StreamService.addStream(
			stream.value.name,
			stream.value.uiRegion._id,
			stream.value.type
		);
		notify({ group: 'success', text: 'Stream deployed successfully' })
		track(EVENTS.STREAM_CREATED, { streamType: stream.value.type })
		// We should refresh the streams list first to get the new stream. Otherwise, the new stream will not be available in the store.
		await store.dispatch('Streams/getStreams', true)
		let routeName = ''
		if (stream.value.type === 'live') { routeName = 'LivestreamManage' }
		if (stream.value.type === 'restream') { routeName = 'SimulcastManage' }
		if (stream.value.type === 'scheduled') { routeName = 'ScheduledStreamManageDashboard' }
		router.push({
			name: routeName,
			params: {
				streamId: newStream._id
			}
		})
	} catch (e) {
		error.value = e.message
		console.log(e);
	} finally {
		isLoading.value = false
	}
}

defineExpose({ toggleSidebar, openForType })
</script>

<template>
	<Sidebar ref="sidebar" :stage="currentStage" @closed="clearData" @opened="onSidebarOpened" @go-to-stage="goToStage"
		:isMultiStage="isMultiStage">
		<template v-if="isMultiStage" v-slot:stage-one>
			<div class="mt-12">
				<h1 class="text-xl !px-4 font-medium mb-6">Create New</h1>
				<Tooltip
					wrapper-class="block w-full"
					:active="atMultistreamLimit"
					wrap-trigger
					:html="true"
					:hide="1000"
					:content="planLimitReachedMessage('Multistream')"
				>
					<SidebarItem
						title="Multistream"
						description="Stream to multiple platforms"
						:icon="IconMultistream"
						:disabled="atMultistreamLimit"
						:isActive="stream.type === 'restream'"
						@click="setStreamType('restream')"
					/>
				</Tooltip>
				<Tooltip
					wrapper-class="block w-full"
					:active="atLivestreamLimit"
					wrap-trigger
					:html="true"
					:hide="1000"
					:content="planLimitReachedMessage('Livestream')"
				>
					<SidebarItem
						title="Live Stream"
						description="Livestream to websites with your player, stream to social media, and convert livestreams to on-demand videos — all in one place"
						:icon="IconLivestream"
						:disabled="atLivestreamLimit"
						:isActive="stream.type === 'live'"
						@click="setStreamType('live')"
					/>
				</Tooltip>
			</div>
		</template>
		<template v-slot:[computedStage]>
			<SidebarTrial v-if="showTrial" :stream-type="stream.type" @close-sidebar="toggleSidebar" />
			<div v-else class="px-6 h-full">
				<h3 class="text-lg font-medium mb-8 md:!mb-2 -mt-[44px] md:mt-0 md:!ml-0 md:!mt-3"
					:class="isMultiStage ? '!ml-6' : '!ml-0'">{{ title }}</h3>
				<p class="text-sm text-surface-8 !mb-4">{{ description }}</p>
				<Alert v-if="error" :text="error" type="error" class="!mb-2" />
				<div class="!mb-6">
					<FloatingInput ref="nameInput" id="add-stream-name" label="Stream name" surface="2" dense
						v-model="stream.name" @keyup.enter="onSubmit" />
				</div>
				<div v-if="shouldShowRegion" class="mb-6">
					<div class="region-field">
						<span class="region-field__label">Ingest region</span>
						<StreamRegion class="region-field__control" :stream="stream" filled
							@switch-stream-region="selectRegion" />
					</div>
					<p class="text-tiny text-surface-7 mt-1 mb-0">Choose the region closest to your encoder for the best ingest quality.</p>
				</div>
				<Tooltip
					wrapper-class="block w-full"
					:active="atSelectedTypeLimit"
					wrap-trigger
					:html="true"
					:hide="1000"
					:content="selectedTypeLimitMessage"
				>
					<FormButton :isLoading="isLoading" class="!mb-3 w-full text-center justify-center" :label="'Create ' + title"
						:disabled="isDisabled" @click="createStream" />
				</Tooltip>
				<FormButton class="w-full text-center justify-center mb-4" type="secondary" label="Cancel"
					@click="toggleSidebar" />
			</div>
		</template>
	</Sidebar>
</template>

<style scoped>
/* Wraps the custom region picker in a Material floating-label field so it
   matches the FloatingInput/FloatingSelect fields used elsewhere in the
   slider. The label stays floated because the picker always shows a value or
   placeholder. StreamRegion's own trigger is neutralised to blend in. */
.region-field {
	position: relative;
	height: 44px;
	border: 1px solid var(--surface-4, #404b72);
	background-color: var(--surface-3, #343e61);
	border-radius: 8px;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.region-field:hover {
	border-color: var(--surface-5, #4f5c87);
}
.region-field:focus-within {
	border-color: var(--c-main, #3e64ff);
	box-shadow: 0 0 0 3px rgba(62, 100, 255, 0.15);
}
.region-field__label {
	position: absolute;
	left: 13px;
	top: 6px;
	font-size: 10px;
	line-height: 1;
	font-weight: 500;
	letter-spacing: 0.02em;
	color: var(--surface-8, #8b93ab);
	pointer-events: none;
	z-index: 2;
}
.region-field__control {
	height: 100%;
}
.region-field__control :deep(> button) {
	height: 100%;
	background-color: transparent;
	padding: 15px 12px 3px 13px;
	border-radius: 8px;
}
.region-field__control :deep(> button:hover) {
	background-color: transparent;
}
</style>
